import * as Sentry from "@sentry/react";

import formatLogError from "~/helpers/formatLogError";
import classifyRtkError, { describeErrorIdentity, extractStatusCode } from "~/helpers/rtkErrorClassification";
import { extractCorrelationId, extractGraphqlErrorCode, isAbortError } from "~/services/graphqlErrorHelpers";
import { buildLoggerFingerprint, severityForClassification } from "~/services/sentryRtkInstrumentation";

const isDev = import.meta.env.DEV;

interface ErrorLogAction {
	payload: {
		code: string;
		detail: string;
		message: string;
		viewed: boolean;
	};
	type: string;
}

type ErrorLogSink = (action: ErrorLogAction) => void;

const maxPendingErrorLogActions = 100;

let errorLogSink: ErrorLogSink | null = null;
const pendingErrorLogActions: ErrorLogAction[] = [];

const deliver = (action: ErrorLogAction): void => {
	try {
		errorLogSink?.(action);
	} catch {
		// Silently ignore dispatch failures (e.g. during app teardown)
	}
};

export function registerErrorLogSink(sink: ErrorLogSink): void {
	errorLogSink = sink;
	while (pendingErrorLogActions.length > 0) {
		const action = pendingErrorLogActions.shift() as ErrorLogAction;
		queueMicrotask(() => deliver(action));
	}
}

function dispatchToErrorLog(message: string, detail: unknown): void {
	const action: ErrorLogAction = {
		payload: {
			code: "logger.error",
			detail: detail === undefined ? "" : detail instanceof Error ? detail.message : formatLogError(detail),
			message,
			viewed: false
		},
		type: "error/addError"
	};

	if (!errorLogSink) {
		pendingErrorLogActions.push(action);
		if (pendingErrorLogActions.length > maxPendingErrorLogActions) pendingErrorLogActions.shift();
		return;
	}

	queueMicrotask(() => deliver(action));
}

const logger = {
	/** Dev-only — stripped in production builds by Vite dead-code elimination */
	log: (...args: unknown[]) => {
		if (isDev) console.log(...args);
	},
	debug: (...args: unknown[]) => {
		if (isDev) console.debug(...args);
	},

	/**
	 * Always logs to console (not dev-only) — used for security-relevant warnings
	 * such as auth state mismatches, corrupted tokens, and OAuth validation failures.
	 */
	warn: (...args: unknown[]) => {
		console.warn(...args);
	},

	/**
	 * Always logs to console, dispatches to the in-app error log, and captures
	 * to Sentry in production.
	 *
	 * Supports two call signatures:
	 *   logger.error(error)
	 *   logger.error(error, context)
	 *   logger.error('Descriptive message', error)
	 *   logger.error('Descriptive message', error, context)
	 */
	error: (errorOrMessage: unknown, errorOrContext?: unknown, context?: Record<string, unknown>) => {
		let error: unknown;
		let ctx: Record<string, unknown> | undefined;
		let message: string | undefined;

		if (typeof errorOrMessage === "string") {
			message = errorOrMessage;
			error = errorOrContext;
			ctx = context;
		} else {
			error = errorOrMessage;
			if (errorOrContext === null || errorOrContext === undefined) {
				ctx = undefined;
			} else if (typeof errorOrContext === "object") {
				ctx = { ...errorOrContext };
			} else {
				// Preserve a non-object second arg (e.g. an id label) as `detail`
				// so it surfaces in the Sentry context rather than being dropped.
				ctx = { detail: errorOrContext };
			}
		}

		const formatted = formatLogError(error);
		if (message) {
			console.error(message, formatted);
		} else {
			console.error(formatted);
		}

		// Dispatch to in-app error log
		dispatchToErrorLog(message ?? (error instanceof Error ? error.message : "Application error"), error);

		if (!isDev) {
			// Aborted requests (unmount/navigation) are expected and should never
			// reach Sentry — drop them at the source.
			if (isAbortError(error)) return;

			const classification = classifyRtkError(error);
			const statusCode = extractStatusCode(error);
			const graphqlCode = extractGraphqlErrorCode(error);
			const correlationId = extractCorrelationId(error);
			const identity = describeErrorIdentity(error);
			const level = severityForClassification(classification);

			Sentry.withScope((scope) => {
				scope.setTag("error.source", "logger");
				scope.setTag("error.classification", classification);
				scope.setTag("logger.unstructured", ctx ? "false" : "true");
				if (statusCode !== undefined) scope.setTag("http.status_code", String(statusCode));
				if (graphqlCode) scope.setTag("graphql.code", graphqlCode);
				if (correlationId) scope.setTag("correlation.id", correlationId);
				scope.setTag("error.identity", identity);
				scope.setFingerprint(buildLoggerFingerprint({ classification, graphqlCode, identity, statusCode }));
				scope.setLevel(level);
				if (ctx) scope.setContext("loggerContext", ctx);
				if (message) scope.setExtra("message", message);

				if (error instanceof Error) {
					Sentry.captureException(error);
				} else if (error !== undefined) {
					// captureException on a non-Error titles the issue "[object Object]"; the identity is the readable name.
					scope.setExtra("rawError", formatted);
					Sentry.captureMessage(message ? `${message} — ${identity}` : identity, level);
				} else if (message) {
					Sentry.captureMessage(message, level);
				}
			});
		}
	}
};

export default logger;
